import React, { useEffect, useState } from "react";
import {
  getCategories,
  getAllProductsByCategory,
  addProducts,
  deleteProductDynamic,
  editProductDynamic,
} from "../../services/productServices";
import { FaPlus, FaMinus, FaTrash, FaEdit } from "react-icons/fa";
import { uploadFile } from "../../services/postService";

function CreateProduct() {
  // -------------------
  // Add‑Product & Edit‐Product state
  // -------------------
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [products, setProducts] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [newProductDescription, setNewProductDescription] = useState("");
  const [newProductDetails, setNewProductDetails] = useState("");
  const [newPictureFile, setNewPictureFile] = useState("");
  const [newMeasurements, setNewMeasurements] = useState([]);

  const [editingProductId, setEditingProductId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDetails, setEditDetails] = useState("");
  const [measurements, setMeasurements] = useState([]);
  const [editPictureLink, setEditPictureLink] = useState("");
  const [editPictureLinkFile, setEditPictureLinkFile] = useState(null);
  const [toast, setToast] = useState({ message: "", isError: false });
  const [loading, setLoading] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    getCategories().then(setCategories).catch(console.error);
  }, []);

  // When a category is picked, clear old products and load new ones
  const handleCategorySelect = async (category) => {
    setSelectedCategory(category.name);
    setEditingProductId(null);
    setProducts([]);
    try {
      const list = await getAllProductsByCategory(category.name);
      setProducts(list || []);
    } catch (err) {
      console.error("Error fetching products", err);
    }
  };

  const addNewMeasurementField = () => {
    setNewMeasurements((prev) => [...prev, { en: "", am: "", rate: "", tempId: Date.now() + Math.random() }]);
  };

  const removeNewMeasurementField = (index) => {
    setNewMeasurements((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNewMeasurementChange = (index, lang, value) => {
    setNewMeasurements((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [lang]: value } : m))
    );
  };

  const addProductToList = () => {
    if (!newProductName.trim()) return;
    setLoading(true);

    setTimeout(() => {
      // Format newMeasurements into array of strings: ["EN / AM / RATE", ...]
      const formattedNewMeasurements = newMeasurements
        .map(({ en, am, rate }) => {
          const e = en.trim();
          const a = am.trim();
          const r = rate.trim();
          // If en or am exist, at least include those; rate may be empty string
          if (e || a || r) {
            // Always join with " / "
            return `${e} / ${a} / ${r}`;
          }
          return null; 
        })
        .filter(Boolean);

      setProducts((prev) => [
        ...prev,
        {
          id: Date.now(), // Temporary client‐side ID
          name: newProductName.trim(),
          description: newProductDescription.trim(),
          details: newProductDetails.trim(),
          pictureFile: newPictureFile, // store the File object
          measurements: formattedNewMeasurements,
          isNew: true,
        },
      ]);

      // Clear “add new product” fields:
      setNewProductName("");
      setNewProductDescription("");
      setNewProductDetails("");
      setNewPictureFile(null);
      setNewMeasurements([]);
      setShowInput(false);
      setLoading(false);
    }, 500); // simulate delay
  };

  // Remove a pending (new) product
  const removeProduct = (idx) =>
    setProducts((prev) => prev.filter((_, i) => i !== idx));

  // Delete an existing product via API
  const handleDeleteExisting = async (prodId) => {
    try {
      await deleteProductDynamic(selectedCategory, prodId);
      setProducts(products.filter((p) => p.id !== prodId));
      setToast({ message: "Product deleted", isError: false });
    } catch (err) {
      console.error("Error deleting product", err);
      setToast({ message: "Delete failed", isError: true });
    }
  };

  const addMeasurementField = () => {
    setMeasurements((prev) => [...prev, { en: "", am: "", rate: "", tempId: Date.now() + Math.random() }]);
  };

  const removeMeasurementField = (index) => {
    setMeasurements((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMeasurementChange = (index, lang, value) => {
    setMeasurements((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [lang]: value } : m))
    );
  };

  const handleEditClick = (prod) => {
    setEditingProductId(prod.id);
    setEditName(prod.name || "");
    setEditDescription(prod.description || "");
    setEditDetails(prod.details || "");
    setEditPictureLink(prod.picture_link || "");

    const existing = [];

    if (typeof prod.measurement === "string") {
      // e.g. "KG / ኪሎ / 0.01, LB / ፓውንድ / 0.02"
      prod.measurement
        .split(",")
        .map((segment) => segment.trim())
        .forEach((m) => {
          const [en = "", am = "", rate = ""] = m
            .split(" / ")
            .map((s) => s.trim("").replace("[", "").replace("]", "").replace('"', ""));
          existing.push({ en, am, rate, tempId: Date.now() + Math.random() });
        });
    }

    setMeasurements(existing);
  };

  const handleSaveEdit = async (prodId) => {
    try {
      let uploadedLink = null;
      if (editPictureLinkFile) {
        const res = await uploadFile(editPictureLinkFile, "Ankuaru_ProductImages");
        uploadedLink = res.filelink;
      }

      // Format the measurements array: ["EN / AM / RATE", ...]
      const formatted = measurements
        .map(({ en, am, rate }) => {
          const e = en.trim();
          const a = am.trim();
          const r = rate.trim();
          if (e || a || r) {
            return `${e} / ${a} / ${r}`;
          }
          return null;
        })
        .filter(Boolean);

      await editProductDynamic({
        categoryName: selectedCategory,
        productId: prodId,
        name: editName,
        description: editDescription,
        details: editDetails,
        pictureLink: uploadedLink ?? editPictureLink,
        measurements: formatted,
      });

      // Clear edit state
      setEditingProductId(null);
      setEditName("");
      setEditDescription("");
      setEditDetails("");
      setEditPictureLinkFile(null);
      setMeasurements([]);
      setToast({ message: "Product updated successfully", isError: false });
    } catch (error) {
      console.error("Error saving product edit:", error);
      setToast({ message: "Failed to update product", isError: true });
    }
  };

  function FilePicker({ existingLink, fileObject, onFileChange }) {
  return (
    <label className="relative inline-flex items-stretch border rounded overflow-hidden">
      {/* Left: “Choose File” */}
      <span className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-sm border-r">
        Choose File
      </span>
      {/* Right: placeholder */}
      <span className="px-2 py-1 text-sm flex-1">
        {fileObject
          ? fileObject.name
          : existingLink
          ? (
            <a
              href={existingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline"
            >
             ${existingLink}
            </a>
          )
          : "No file chosen"}
      </span>
      {/* Invisible real input */}
      <input
        type="file"
        accept="image/*"
        onChange={e => onFileChange(e.target.files[0] || null)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </label>
  );
}

  const handleCancelEdit = () => setEditingProductId(null);

  const saveProducts = async () => {
    setLoading(true);
    try {
      for (const prod of products) {
        if (prod.isNew) {
          let pictureLink = null;
          if (prod.pictureFile) {
            const uploadResponse = await uploadFile(
              prod.pictureFile,
              "product-images"
            );
            pictureLink = uploadResponse.filelink;
          }
          await addProducts(
            // 1st argument: only spread "name" (and any other simple fields, but not description/details/measurement)
            { name: prod.name },
            // 2nd argument:
            selectedCategory,
            // 3rd argument:
            pictureLink,
            // 4th argument: description
            prod.description,
            // 5th argument: details
            prod.details,
            // 6th argument: measurements array
            prod.measurements
          );
        }
      }

      setToast({ message: "Products saved!", isError: false });
      // window.location.reload();
    } catch (err) {
      console.error("Error saving products", err);
      setToast({ message: "Error saving products", isError: true });
    } finally {
      setLoading(false);
    }
  };

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast.message) return;
    const timer = setTimeout(
      () => setToast({ message: "", isError: false }),
      3000
    );
    return () => clearTimeout(timer);
  }, [toast]);

  return (
    <div className="min-h-screen mx-20 pt-14 pl-40 pr-40 pb-20 bg-gray">
      <h1 className="text-2xl font-rblack font-bold mb-5">
        Add & Edit Products
      </h1>
      {toast.message && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded shadow z-50
          ${
            toast.isError
              ? "bg-red-100 text-red-700 border-red-400"
              : "bg-green-100 text-green-700 border-green-400"
          }`}
        >
          {toast.message}
          <button
            onClick={() => setToast({ message: "", isError: false })}
            className="ml-4 font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Category selector */}
      <h2 className="mb-3">Select a Category</h2>
      <div className="flex flex-wrap gap-3 mb-10">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleCategorySelect(cat)}
            className={`rounded-2xl shadow-sm px-5 py-2 ${
              selectedCategory === cat.name
                ? "bg-green-500 text-white"
                : "bg-white text-black"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Products list */}
      {selectedCategory && (
        <div>
          <h3 className="mb-3">Products in {selectedCategory}</h3>
          <div className="flex flex-wrap gap-3 items-start mb-4 ">
            {products.length ? (
              products.map((prod, idx) => (
                <div
                  key={prod.id}
                  className="relative bg-white rounded-2xl shadow-sm p-4 pt-5 pr-12"
                >
                  {editingProductId === prod.id ? (
                    <div className="flex flex-col gap-3">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="border p-2 rounded w-full"
                        placeholder="Product name"
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="border p-2 rounded w-full h-16"
                        placeholder="Description"
                      />
                     <div className="flex items-center gap-2">
 {/* === REPLACE THIS WHOLE INPUT WITH FilePicker === */}
  <FilePicker
    existingLink={editPictureLink}
    fileObject={editPictureLinkFile}
    onFileChange={file => setEditPictureLinkFile(file)}
  />

  {/* If there’s already a pictureLink (and user hasn’t picked a new file), show it */}
 

  {/* If they did pick a new file, show its name */}
  {editPictureLinkFile && (
    <span className="text-gray-600 text-sm">
      {editPictureLinkFile.name}
    </span>
  )}
</div>
                      <label className="font-medium">Measurements</label>
                      {measurements.length === 0 ? (
                        <button
                          onClick={addMeasurementField}
                          className="flex items-center gap-1 text-blue-500"
                        >
                          <FaPlus /> Add Measurement
                        </button>
                      ) : (
                        <>
                          {measurements.map((m, i) => (
                            <div key={m.tempId || `measurement-${i}`} className="flex items-center gap-2">
                              {/* Prefilled “EN” field */}
                              <input
                                type="text"
                                value={m.en}
                                onChange={(e) =>
                                  handleMeasurementChange(
                                    i,
                                    "en",
                                    e.target.value
                                  )
                                }
                                placeholder="e.g. KG"
                                className="border p-2 rounded flex-1"
                              />
                              {/* Prefilled “AM” field */}
                              <input
                                type="text"
                                value={m.am}
                                onChange={(e) =>
                                  handleMeasurementChange(
                                    i,
                                    "am",
                                    e.target.value
                                  )
                                }
                                placeholder="e.g. ኪሎ"
                                className="border p-2 rounded flex-1"
                              />
                              <input
                                type="text"
                                value={m.rate}
                                onChange={(e) =>
                                  handleMeasurementChange(
                                    i,
                                    "rate",
                                    e.target.value
                                  )
                                }
                                placeholder="Conversion to quintal"
                                className="border p-2 rounded flex-1"
                              />
                              <FaMinus
                                onClick={() => removeMeasurementField(i)}
                                className="cursor-pointer text-red-400"
                              />
                            </div>
                          ))}
                          <button
                            onClick={addMeasurementField}
                            className="flex items-center gap-1 text-blue-500"
                          >
                            <FaPlus /> Add Measurement
                          </button>
                        </>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(prod.id)}
                          className="bg-blue-500 text-white px-4 py-2 rounded"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="bg-gray-300 text-black px-4 py-2 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col text-left">
                        <span className="font-semibold">{prod.name}</span>
                        {prod.description && (
                          <span className="text-sm text-gray-600">
                            {prod.description.split(" ").slice(0, 2).join(" ")}
                          </span>
                        )}
                        {prod.pictureLink && (
                          <img
                            src={prod.pictureLink}
                            alt={prod.name}
                            className="w-16 h-16 object-cover mt-2"
                          />
                        )}
                      </div>
                      {/* Action icons */}
                      <div className="absolute top-2 right-2 flex gap-2">
                        {prod.isNew ? (
                          <FaMinus
                            onClick={() => removeProduct(idx)}
                            className="cursor-pointer text-red-400 transition-transform duration-200 hover:scale-110"
                          />
                        ) : (
                          <>
                            <FaEdit
                              onClick={() => handleEditClick(prod)}
                              className="cursor-pointer text-blue-500 transition-transform duration-200 hover:scale-110"
                            />
                            <FaTrash
                              onClick={() => handleDeleteExisting(prod.id)}
                              className="cursor-pointer text-red-500 transition-transform duration-200 hover:scale-110"
                            />
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))
            ) : (
              <p>No products added yet.</p>
            )}

            {/* Add new product button */}
            <button
              onClick={() => setShowInput(!showInput)}
              className="rounded-full bg-blue-500 text-white p-2 shadow-sm flex items-center justify-center"
            >
              <FaPlus />
            </button>
          </div>

          {/* New-product form */}

          {showInput && (
            <div className="flex flex-col gap-3 mb-6 bg-white p-4 rounded-2xl shadow-sm">
              <input
                type="text"
                placeholder="Enter product name"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                className="border p-2 rounded w-full"
              />
              <textarea
                placeholder="Enter product description"
                value={newProductDescription}
                onChange={(e) => setNewProductDescription(e.target.value)}
                className="border p-2 rounded w-full h-20"
              />

              {/* === REPLACE THIS WHOLE INPUT WITH FilePicker === */}
  <FilePicker
    existingLink={editPictureLink}
    fileObject={editPictureLinkFile}
    onFileChange={file => setEditPictureLinkFile(file)}
  />

              <label className="font-medium">Measurements</label>
              {newMeasurements.length === 0 ? (
                <button
                  onClick={addNewMeasurementField}
                  className="flex items-center gap-1 text-blue-500"
                >
                  <FaPlus /> Add Measurement
                </button>
              ) : (
                <>
                  {newMeasurements.map((m, i) => (
                    <div key={m.tempId || `new-measurement-${i}`} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={m.en}
                        onChange={(e) =>
                          handleNewMeasurementChange(i, "en", e.target.value)
                        }
                        placeholder="e.g. KG"
                        className="border p-2 rounded flex-1"
                      />
                      <input
                        type="text"
                        value={m.am}
                        onChange={(e) =>
                          handleNewMeasurementChange(i, "am", e.target.value)
                        }
                        placeholder="e.g. ኪሎ"
                        className="border p-2 rounded flex-1"
                      />
                      <input
                        type="text"
                        value={m.rate}
                        onChange={(e) =>
                          handleNewMeasurementChange(i, "rate", e.target.value)
                        }
                        placeholder="Conversion to quintal"
                        className="border p-2 rounded flex-1"
                      />
                      <FaMinus
                        onClick={() => removeNewMeasurementField(i)}
                        className="cursor-pointer text-red-400"
                      />
                    </div>
                  ))}
                  <button
                    onClick={addNewMeasurementField}
                    className="flex items-center gap-1 text-blue-500"
                  >
                    <FaPlus /> Add Measurement
                  </button>
                </>
              )}

              <button
                onClick={addProductToList}
                className={`bg-green-500 text-white px-4 py-2 rounded self-start ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? "Adding..." : "Add Product"}
              </button>
            </div>
          )}

          {/* Confirm new products */}
          {products.some((p) => p.isNew) && (
            <div className="flex justify-end">
              <button
                onClick={saveProducts}
                className={`bg-green-500 hover:bg-green-500/50 text-white px-16 py-2 rounded shadow ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? "Saving..." : "Confirm"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CreateProduct;
